import React, { useState } from 'react';
import { researchToolsApi } from '../api';
import type { QuickSurveyDelivered, QuickSurveyDraft } from '../types';
import { SurveyHubHero } from './SurveyHubHero';
import '../survey-hub.css';

interface Props {
  onBack: () => void;
}

/** Quick service survey — template quick() + quickDraft(). */
export const QuickServiceSurveyView: React.FC<Props> = ({ onBack }) => {
  const [command, setCommand] = useState(
    'Создай короткий анонимный опрос для докторантов 1 курса на русском языке: понять, какие вопросы по индивидуальному плану и научному руководству остались непонятными.',
  );
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<QuickSurveyDraft | null>(null);
  const [delivering, setDelivering] = useState(false);
  const [delivered, setDelivered] = useState<QuickSurveyDelivered | null>(null);

  const handleGenerateDraft = async () => {
    if (!command.trim()) {
      alert('Введите команду для формирования экспресс-опроса');
      return;
    }
    setLoading(true);
    try {
      const res = await researchToolsApi.createQuickSurveyDraft(command);
      setDraft(res);
      setDelivered(null);
    } catch {
      alert('Ошибка при извлечении параметров опроса');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveAndDeliver = async () => {
    if (!draft) return;
    setDelivering(true);
    try {
      const res = await researchToolsApi.approveAndDeliverQuickSurvey({
        command: draft.raw_command,
        extracted_target: draft.extracted_target,
        questions: draft.questions,
      });
      setDelivered(res);
    } catch {
      alert('Ошибка при доставке опроса');
    } finally {
      setDelivering(false);
    }
  };

  const target = draft?.extracted_target;

  return (
    <div className="survey-hub-content">
      {!draft ? (
        <>
          <SurveyHubHero
            title="Быстрый служебный опрос"
            description="Владелец или уполномоченная роль может дать короткую команду текстом или голосом. Система уточняет аудиторию только если параметр не сказан."
          />
          <section className="survey-hub-panel survey-hub-ai-draft">
            <header>
              <div className="grow">
                <h3>Команда</h3>
                <p>Текст или голос</p>
              </div>
              <span className="survey-hub-chip gold">Уполномочено</span>
            </header>
            <div className="survey-hub-field full">
              <label>Сообщение служебному консультанту</label>
              <textarea value={command} onChange={(e) => setCommand(e.target.value)} rows={5} />
            </div>
            <div className="survey-hub-actions">
              <button type="button" className="survey-hub-btn" onClick={onBack}>
                ← Назад
              </button>
              <button
                type="button"
                className="survey-hub-btn"
                onClick={() => {
                  setCommand(
                    'Сформировать экспресс-опрос для докторантов 1 курса по заполнению индивидуального плана',
                  );
                }}
              >
                🎙 Записать голосовое
              </button>
              <button
                type="button"
                className="survey-hub-btn primary"
                onClick={handleGenerateDraft}
                disabled={loading}
              >
                {loading ? 'Формирование…' : 'Сформировать черновик →'}
              </button>
            </div>
          </section>
        </>
      ) : (
        <>
          <SurveyHubHero
            title="Черновик ещё не опубликован"
            description="До явного «Утвердить и отправить» никто из целевой аудитории не получает форму."
          />
          <section className="survey-hub-panel survey-hub-ai-draft">
            <header>
              <div className="grow">
                <h3>Черновик</h3>
                <p>
                  Аудитория:{' '}
                  {[target?.audience, target?.course, target?.language, target?.mode]
                    .filter(Boolean)
                    .join(' · ') || 'Докторанты · 1 курс · RU · анонимно'}
                </p>
              </div>
              <span className="survey-hub-chip gold">
                {delivered ? 'Отправлено' : 'Черновик / не отправлено'}
              </span>
            </header>

            {(draft.questions || []).map((q, i) => {
              const typeRu: Record<string, string> = {
                text: 'Открытый текст',
                single: 'Одиночный выбор',
                multi: 'Множественный выбор',
                scale: 'Шкала',
                matrix: 'Матрица',
                date: 'Дата',
              };
              return (
                <div key={i} className="survey-hub-question">
                  <strong>
                    {i + 1}. {q.title || q.prompt}
                  </strong>
                  <small>{typeRu[q.question_type] || q.question_type}</small>
                </div>
              );
            })}

            {!delivered ? (
              <div className="survey-hub-actions">
                <button type="button" className="survey-hub-btn" onClick={() => setDraft(null)}>
                  Редактировать
                </button>
                <button
                  type="button"
                  className="survey-hub-btn primary"
                  onClick={handleApproveAndDeliver}
                  disabled={delivering}
                >
                  {delivering ? 'Отправка…' : 'Утвердить и отправить целевой группе'}
                </button>
              </div>
            ) : null}
          </section>

          {delivered ? (
            <section className="survey-hub-panel">
              <header>
                <div className="grow">
                  <h3>Доставка целевой группе</h3>
                  <p>После утверждения опрос появляется у выбранных обучающихся.</p>
                </div>
                <span className="survey-hub-chip ok">Доставлено</span>
              </header>
              <div className="survey-hub-target-grid">
                <div className="survey-hub-target">
                  <small>Получатели</small>
                  <b>{delivered.recipients_count ?? 24}</b>
                </div>
                <div className="survey-hub-target">
                  <small>В кабинете</small>
                  <b>
                    {delivered.in_app_delivered}/{delivered.recipients_count}
                  </b>
                </div>
                <div className="survey-hub-target">
                  <small>E-mail</small>
                  <b>{delivered.email_queued ? `В очереди · ${delivered.email_queued}` : '—'}</b>
                </div>
              </div>
              <div className="survey-hub-actions">
                <button type="button" className="survey-hub-btn" onClick={onBack}>
                  ← В меню инструментов
                </button>
              </div>
            </section>
          ) : (
            <section className="survey-hub-panel">
              <header>
                <div className="grow">
                  <h3>Целевая аудитория из команды</h3>
                </div>
              </header>
              <div className="survey-hub-target-grid">
                <div className="survey-hub-target">
                  <small>Курс</small>
                  <b>{target?.course || '1 курс'}</b>
                </div>
                <div className="survey-hub-target">
                  <small>Аудитория</small>
                  <b>{target?.audience || 'Докторанты'}</b>
                </div>
                <div className="survey-hub-target">
                  <small>Язык</small>
                  <b>{target?.language || 'RU'}</b>
                </div>
                <div className="survey-hub-target">
                  <small>Режим</small>
                  <b>{target?.mode === 'VERIFIED' ? 'С подтверждением' : 'Анонимно'}</b>
                </div>
                <div className="survey-hub-target">
                  <small>Программа</small>
                  <b>{target?.program || 'Все программы'}</b>
                </div>
                <div className="survey-hub-target">
                  <small>Группа</small>
                  <b>{target?.group || 'Все группы 1 курса'}</b>
                </div>
              </div>
              <div className="survey-hub-note" style={{ marginTop: 10 }}>
                Все необходимые параметры уже указаны; дополнительных блокирующих вопросов нет.
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};
